/**
 * The deck's shape (strategy v3, Figma 13 Strategy, Zone plate · APPROVED),
 * slide for slide as the plate labels it ("02 · 1 Front · Chapter opener"):
 * 1 Front (the cover, the chapter opener, the one page, the brief), chapters 2
 * to 13 (each opened by its chapter opener), After (how we did this, sources
 * and the record) and Back (the close).
 * The order is the same for every strategy; a slide with nothing to draw drops.
 */
import type { StrategyChapterKey, StrategyListItem } from "@/types/strategy.type";
import type { StrategySlide } from "../components/strategySlides";

export interface ChapterSpec {
  key: StrategyChapterKey;
  /** The chapter's fixed place in the story (1–13). */
  number: number;
  /** The opener's Crake lines (one or two). */
  title: readonly string[];
  /**
   * The opener's fixed line (Chapter / Question): the CEO's line for the
   * chapter in the framework's §3, word for word ("the question each chapter
   * answers matches section 3"); chapter 4's is the approved Figma opener's.
   */
  question: string;
  /** The brand eyebrow on its slides ("FATAFEAT · THE BRIEF"). */
  eyebrow: string;
  /** Its name in a slide's foot ("… · The brief"). */
  foot: string;
}

export const CHAPTERS: readonly ChapterSpec[] = [
  { key: "brief", number: 1, title: ["The brief."], question: "What was asked, and what social must achieve.", eyebrow: "The brief", foot: "The brief" },
  { key: "industry", number: 2, title: ["The industry."], question: "Where the industry is, where it is heading, and what is normal on social.", eyebrow: "The industry", foot: "The industry" },
  { key: "audience", number: 3, title: ["The audience."], question: "Who we speak to, what they care about, and how they use social.", eyebrow: "The audience", foot: "The audience" },
  { key: "brand", number: 4, title: ["The brand", "today."], question: "Which of last year’s posts did the work, and why?", eyebrow: "The brand today", foot: "The brand today" },
  { key: "competitors", number: 5, title: ["The", "competitors."], question: "Who holds this audience’s attention, and how.", eyebrow: "The competitors", foot: "The competitors" },
  { key: "culture", number: 6, title: ["Culture and", "conversation."], question: "What is moving now, and what people say.", eyebrow: "Culture and conversation", foot: "Culture and conversation" },
  { key: "insight", number: 7, title: ["The insight", "and the idea."], question: "What everything hangs on.", eyebrow: "The insight and the idea", foot: "The insight and the idea" },
  { key: "goals", number: 8, title: ["Goals and", "targets."], question: "What will change, by how much, by when.", eyebrow: "Goals and targets", foot: "Goals and targets" },
  { key: "platforms", number: 9, title: ["Platforms."], question: "What each platform is for.", eyebrow: "Platforms", foot: "Platforms" },
  { key: "content", number: 10, title: ["The content."], question: "What we make, again and again.", eyebrow: "The content", foot: "The content" },
  { key: "year", number: 11, title: ["The year."], question: "When we show up, and how often.", eyebrow: "The year", foot: "The year" },
  { key: "creators", number: 12, title: ["Creators and", "community."], question: "Who speaks with us, and how we look after the people who follow.", eyebrow: "Creators and community", foot: "Creators and community" },
  { key: "measuring", number: 13, title: ["Measuring", "and learning."], question: "How we report, learn and improve.", eyebrow: "Measuring and learning", foot: "Measuring and learning" },
] as const;

export const CHAPTER_COUNT = CHAPTERS.length;

/** "The brand today", "Culture and conversation": the opener's title as one name. */
export const chapterName = (spec: ChapterSpec) => spec.title.join(" ").replace(/\.$/, "");

export const chapterSpec = (key: string | null | undefined): ChapterSpec | undefined =>
  CHAPTERS.find((c) => c.key === key);

/** The deck's groups, as the plate labels them: thirteen numbered chapters, After, Back. */
export type DeckGroupId =
  | "front" | "industry" | "audience" | "brand" | "competitors" | "culture" | "insight"
  | "goals" | "platforms" | "content" | "year" | "creators" | "measuring" | "after" | "back";

export interface DeckGroupSpec {
  id: DeckGroupId;
  /** The chapter number on the plate (1–13); none for After and Back. */
  number?: number;
  name: string;
}

export const DECK_GROUPS: readonly DeckGroupSpec[] = [
  { id: "front", number: 1, name: "Front" },
  { id: "industry", number: 2, name: "Industry" },
  { id: "audience", number: 3, name: "Audience" },
  { id: "brand", number: 4, name: "Brand" },
  { id: "competitors", number: 5, name: "Competitors" },
  { id: "culture", number: 6, name: "Culture" },
  { id: "insight", number: 7, name: "Insight" },
  { id: "goals", number: 8, name: "Goals" },
  { id: "platforms", number: 9, name: "Platforms" },
  { id: "content", number: 10, name: "Content" },
  { id: "year", number: 11, name: "Year" },
  { id: "creators", number: 12, name: "Creators" },
  { id: "measuring", number: 13, name: "Measuring" },
  { id: "after", name: "After" },
  { id: "back", name: "Back" },
];

/** "6 Culture", "After". */
export const groupLabel = (spec: DeckGroupSpec) => (spec.number ? `${spec.number} ${spec.name}` : spec.name);

export interface DeckGroup {
  spec: DeckGroupSpec;
  /** Its slides, in deck order. */
  slides: StrategySlide[];
}

export interface DeckShape {
  /** The deck in reading order (what the page numbers count). */
  story: StrategySlide[];
  /** The groups that have a slide, in plate order. */
  groups: DeckGroup[];
}

export function shapeDeck(slides: StrategySlide[]): DeckShape {
  const groups = DECK_GROUPS.map((spec) => ({ spec, slides: slides.filter((slide) => slide.group === spec.id) }))
    .filter((group) => group.slides.length);
  return { story: slides, groups };
}

/** The group a slide sits in. */
export function groupOf(shape: DeckShape, slide: StrategySlide): DeckGroup | null {
  return shape.groups.find((group) => group.spec.id === slide.group) ?? null;
}

/** The slide's page number: its place in the deck. */
export function slideNumber(shape: DeckShape, slide: StrategySlide): string {
  const at = shape.story.indexOf(slide);
  return at >= 0 ? String(at + 1) : "";
}

/** "v2" → 2; anything else → null. */
export function versionNumber(version: string | null | undefined): number | null {
  const match = /^v(\d+)$/.exec((version ?? "").trim());
  return match ? Number(match[1]) : null;
}

function versionWords(version: string | null | undefined): string {
  const n = versionNumber(version);
  return n == null ? (version ?? "").toUpperCase() : `VERSION ${n}`;
}

/** The status pill (Strategy / Status): three statuses, nothing else. */
export function statusPillText(
  strategy: Pick<StrategyListItem, "status" | "version">,
  opts: { empty?: boolean; live?: "ACTIVE" | "LIVE" } = {},
): string {
  if (strategy.status === "active") return `${opts.live ?? "ACTIVE"} · ${versionWords(strategy.version)}`;
  if (strategy.status === "archived") return `ARCHIVED · ${versionWords(strategy.version)}`;
  if (opts.empty) return "NOT STARTED";
  return `DRAFT · ${versionWords(strategy.version)}`;
}

const DAY_MONTH = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", timeZone: "UTC" });
const DAY_MONTH_YEAR = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

function parse(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "7 July". */
export function dayMonth(value: string | null | undefined): string {
  const date = parse(value);
  return date ? DAY_MONTH.format(date) : "";
}

/** "7 July 2025". */
export function dayMonthYear(value: string | null | undefined): string {
  const date = parse(value);
  return date ? DAY_MONTH_YEAR.format(date) : "";
}

/** "2026 to 2027", or "2026" for a period inside one year. */
export function periodYears(start: string | null | undefined, end: string | null | undefined): string {
  const from = start?.slice(0, 4);
  const to = end?.slice(0, 4);
  if (!from || !to) return "";
  return from === to ? from : `${from} to ${to}`;
}

/** "day 92 of 365" while the period runs; null before or after it. */
export function periodDay(start: string | null | undefined, end: string | null | undefined, now = new Date()): string | null {
  const from = parse(start);
  const to = parse(end);
  if (!from || !to) return null;
  const day = 24 * 60 * 60 * 1000;
  const total = Math.round((to.getTime() - from.getTime()) / day) + 1;
  const today = Math.floor((now.getTime() - from.getTime()) / day) + 1;
  if (today < 1 || today > total) return null;
  return `day ${today} of ${total}`;
}

/** "today", "yesterday", "on Monday" (this week), else "on 7 July". */
export function relativeDay(value: string | null | undefined, now = new Date()): string {
  const date = parse(value);
  if (!date) return "";
  const start = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const days = Math.round((start(now) - start(date)) / (24 * 60 * 60 * 1000));
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `on ${new Intl.DateTimeFormat("en-GB", { weekday: "long", timeZone: "UTC" }).format(date)}`;
  return `on ${dayMonth(value)}`;
}
